import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useBuildInfo } from '@/hooks/useBuildInfo'

export interface VersionInfo {
  buildNumber: string
  buildSha: string
  buildDate: string
  appVersion: string
}

const LS_DISMISSED_SHA = 'atlast-admin.version-update.dismissed-sha'
const SS_PENDING_RELOAD = 'atlast-admin.version-update.pending-reload'
const SS_EXPECTED_SHA = 'atlast-admin.version-update.expected-sha'
const SS_NEW_VERSION = 'atlast-admin.version-update.new-version'

const POLL_INTERVAL_MS = 30 * 60 * 1000

function safeLocalGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.warn('versionUpdate: localStorage.getItem failed', error)
    return null
  }
}

function safeLocalSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch (error) {
    console.warn('versionUpdate: localStorage.setItem failed', error)
    return false
  }
}

function safeLocalRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('versionUpdate: localStorage.removeItem failed', error)
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch (error) {
    console.warn('versionUpdate: sessionStorage.getItem failed', error)
    return null
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch (error) {
    console.warn('versionUpdate: sessionStorage.setItem failed', error)
  }
}

function safeSessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    console.warn('versionUpdate: sessionStorage.removeItem failed', error)
  }
}

export const useVersionUpdateStore = defineStore('versionUpdate', () => {
  const { buildSha, buildNumber } = useBuildInfo()

  const latestVersion = ref<VersionInfo | null>(null)
  const dismissedSha = ref<string | null>(safeLocalGet(LS_DISMISSED_SHA))
  const justUpdatedToVersion = ref<string | null>(null)

  let initialised = false
  let isChecking = false
  let intervalId: ReturnType<typeof setInterval> | null = null
  let visibilityHandler: (() => void) | null = null

  const hasNewVersion = computed<boolean>(() => {
    const latest = latestVersion.value
    if (!latest) return false

    const currentSha = buildSha.value
    const latestSha = latest.buildSha

    if (currentSha && latestSha) {
      return currentSha !== latestSha
    }
    return buildNumber.value !== latest.buildNumber
  })

  const shouldShowHeadsUp = computed<boolean>(() => {
    if (!hasNewVersion.value) return false
    return dismissedSha.value !== latestVersion.value?.buildSha
  })

  function reconcileDismissal(): void {
    if (dismissedSha.value && dismissedSha.value === buildSha.value) {
      safeLocalRemove(LS_DISMISSED_SHA)
      dismissedSha.value = null
    }
  }

  function consumePostReloadConfirmation(): void {
    const pending = safeSessionGet(SS_PENDING_RELOAD)
    if (pending !== '1') return

    const expectedSha = safeSessionGet(SS_EXPECTED_SHA)
    const newVersion = safeSessionGet(SS_NEW_VERSION)

    safeSessionRemove(SS_PENDING_RELOAD)
    safeSessionRemove(SS_EXPECTED_SHA)
    safeSessionRemove(SS_NEW_VERSION)

    if (buildSha.value === expectedSha && newVersion && newVersion.length > 0) {
      justUpdatedToVersion.value = newVersion
    }
  }

  async function checkForUpdates(): Promise<void> {
    if (isChecking) return
    isChecking = true

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      if (!response.ok) {
        console.error('versionUpdate: non-ok response', response.status)
        return
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        console.error('versionUpdate: unexpected content-type', contentType)
        return
      }

      const parsed = (await response.json()) as VersionInfo
      latestVersion.value = parsed
    } catch (error) {
      console.error('versionUpdate: checkForUpdates failed', error)
    } finally {
      isChecking = false
    }
  }

  function dismissCurrent(): void {
    if (!latestVersion.value) return
    const sha = latestVersion.value.buildSha
    safeLocalSet(LS_DISMISSED_SHA, sha)
    dismissedSha.value = sha
  }

  function markPendingReload(): void {
    if (!latestVersion.value) return
    safeSessionSet(SS_PENDING_RELOAD, '1')
    safeSessionSet(SS_EXPECTED_SHA, latestVersion.value.buildSha)
    safeSessionSet(SS_NEW_VERSION, latestVersion.value.appVersion)
  }

  function clearJustUpdated(): void {
    justUpdatedToVersion.value = null
  }

  function init(): void {
    if (initialised) return
    initialised = true

    reconcileDismissal()
    consumePostReloadConfirmation()

    void checkForUpdates()

    intervalId = setInterval(() => {
      void checkForUpdates()
    }, POLL_INTERVAL_MS)

    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdates()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)
  }

  // Test-only: tears down listeners + interval so tests don't leak cross-test state.
  function _dispose(): void {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    initialised = false
  }

  return {
    latestVersion,
    dismissedSha,
    justUpdatedToVersion,
    hasNewVersion,
    shouldShowHeadsUp,
    init,
    checkForUpdates,
    dismissCurrent,
    markPendingReload,
    clearJustUpdated,
    _dispose,
  }
})
